import useAppState from '../../../common/state/appState';
import { KmxContainer } from '../../../components/KmxContainer';
import { QuickFilters } from '@kmx/quick-filters';
import { useEffect } from 'react';
import { ANALYTICS_CONSTANTS, trackAnchorClick } from '../../../utilities/analytics';
import { IQuickLink } from '../../../types/quickLinks';
import GlobalStyles from '@mui/material/GlobalStyles';
import { CarouselSkipLink } from '../../../components/CarouselSkipLink';
import HomePageExperience from '../hero/store/HomePageExperience';

type TrimmedQuickLink = Pick<IQuickLink, 'queryKey' | 'displayText' | 'url' | 'displayOrder' | 'highlight'>;

const QuickLinksContainer = () => {
    const getQuickLinks = useAppState((state) => state.getQuickLinks);
    const loading = useAppState((state) => state.isLoading);
    const quickLinks = useAppState((state) => state.quickLinks);
    const quickLinksLoaded = useAppState((state) => state.quickLinksLoaded);
    const serveUFTContentEnabled = useAppState((state) => state.serveUFTContent);
    const experience = useAppState((state) => state.experience);
    const skipLinkHref = experience === HomePageExperience.FirstTimeVisitor ? '#hero' : '#pre-viewed-vehicles-carousel';

    // loading is for homebase and recently viewed vehicles which powers quick links
    // Only try to load quick links once
    useEffect(() => {
        if (!serveUFTContentEnabled || quickLinksLoaded || loading) {
            return;
        }

        getQuickLinks();
    }, [serveUFTContentEnabled, loading, quickLinksLoaded, getQuickLinks]);

    const handleQuickFilterClick = (
        clickEvent: React.MouseEvent<HTMLButtonElement>,
        { queryKey, displayText, url, displayOrder, highlight }: TrimmedQuickLink,
    ) => {
        const taggingEvent = ['shop_all', 'sell_my_car', 'finance', 'research'].includes(queryKey)
            ? ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA
            : ANALYTICS_CONSTANTS.EVENTS.CLICK.SEARCH_INITIATED;

        trackAnchorClick({
            clickEvent,
            taggingEvent,
            title: displayText,
            // We have an incorrectly defined type in an external package that doesn't have displayOrder
            // They do, so we are asserting that it exists.
            position: `quick links:${displayOrder! + 1}:${queryKey}${highlight ? `:${highlight}` : ''}`,
            href: url,
            page: 'Homepage',
            search: {
                initiationMethod: `Homepage|${displayText}|quick links`,
            },
        });
    };

    if (!serveUFTContentEnabled) {
        return <GlobalStyles styles={{ '#quick-links': { display: 'none !important' } }} />;
    }

    if (loading || !quickLinksLoaded) {
        return null;
    }

    return (
        <KmxContainer
            maxWidth="xl"
            sx={{
                pt: { sm: 2 },
            }}
        >
            <CarouselSkipLink id="skip__quick-links" href={skipLinkHref} text="Skip Quick Links" />
            <QuickFilters
                data={quickLinks}
                pageType="home"
                onQuickFilterClick={handleQuickFilterClick}
                stackSx={(theme) => ({
                    mx: { xl: `calc((100vw - ${theme.breakpoints.values.xl}px) / -2 - ${theme.spacing(8)})` },
                    pl: { xl: `calc((100vw - ${theme.breakpoints.values.xl}px) / 2 + ${theme.spacing(8)})` },
                    pr: { xl: 0 },
                })}
                containerId="quick-links"
            />
        </KmxContainer>
    );
};

export default QuickLinksContainer;
